import {fromJS} from 'immutable';
import pick from 'lodash/pick';
import {QuestionStatus} from 'gql/types';
import {
  SEARCH_PARAMETERS,
  getDefaultParameters
} from 'client/Dennis/Content/Queue/QueueSearchPanel/URLSearchParameters';
import appStore from 'client/AppStore';
import {history} from 'client/history';

/**
 * Returns a question set path with no questionSetId or questionId params, plus the current search.
 */
export function getQueueNewQuestionSetPath() {
  return `/dennis/content/queue${(appStore.routerProps.location as {search?: string})?.search ?? ''}`;
}

/**
 * Gets the edit path for a question set (and question) and appends the current search. This method is used for edit links presented within the queue.
 *
 * @param questionSetId
 * @param questionId
 */
export function getQueueQuestionEditPath(questionSetId: string, questionId?: string) {
  const path = `/dennis/content/queue/${questionSetId}`;
  return (
    (questionId ? `${path}/${questionId}` : path) +
    ((appStore.routerProps.location as {search?: string})?.search ?? '')
  );
}
/**
 * Returns the edit path for a given question set (and question).
 *
 * @param root0
 * @param root0.questionSetId
 * @param root0.questionId
 * @param root0.customSearch
 */
export function getQuestionEditPath({
  questionSetId,
  questionId,
  customSearch = {}
}: {
  questionSetId: string;
  questionId?: string;
  customSearch?: Record<string, string | number | undefined>;
}) {
  const questionSetPath = `/dennis/content/queue/${questionSetId}`;
  const path = questionId ? `${questionSetPath}/${questionId}` : questionSetPath;
  return Object.entries({...getDefaultParameters(), ...customSearch}).reduce(
    (acc, [param, value], i, arr) => {
      if (value === undefined) {
        return acc;
      }
      return `${acc}${param}=${value}${i + 1 !== arr.length ? '&' : ''}`;
    },
    `${path}?`
  );
}

/**
 * Returns the questionSetId and questionId URL params. We were previously using appStore.routerProps.params to get
 * these, but when creating new question sets, it's possible that the router props are out of sync with the URL
 * (e.g. in the event of a pushState). This ensures we're sourcing the params from what's currently in the URL.
 */
export function getQuestionSetParams(): {questionSetId?: string; questionId?: string} {
  const basePath = `/dennis/content/queue`;
  const {pathname} = history.getCurrentLocation();
  return pathname
    .replace(basePath, '')
    .split('/')
    .filter((item: string) => item !== '')
    .reduce((acc: {questionSetId?: string; questionId?: string}, id: string, index: number) => {
      if (index === 0) {
        acc.questionSetId = id;
      }
      if (index === 1) {
        acc.questionId = id;
      }
      return acc;
    }, {});
}

export const STATUSES = fromJS([
  {
    name: 'All',
    id: ''
  },
  {
    name: 'Rejected',
    id: QuestionStatus.Rejected
  },
  {
    name: 'Draft',
    id: QuestionStatus.Draft
  },
  {
    name: 'In Review',
    id: QuestionStatus.Pending
  },
  {
    name: 'Published',
    id: QuestionStatus.Published
  },
  {
    name: 'Hidden',
    id: QuestionStatus.Hidden
  },
  {
    name: 'Archived',
    id: QuestionStatus.Archived
  }
]);

export function getQueueFiltersFromQuery(query: Record<string, any>) {
  return pick(query, Object.keys(SEARCH_PARAMETERS));
}
